import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { isRelativeTimeDiffValue } from "@doitintl/cloudflow-commons";
import {
  BuiltInVariableType,
  type RelativeTimeDiffValue,
  TimeFrequency,
  type TimestampApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocalizationProvider, StaticDateTimePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { type FieldInputProps, useFormikContext } from "formik";
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";
import omit from "lodash/omit";
import { DateTime } from "luxon";

import { useToggle } from "../../../../../utils/useToggle";
import { TimestampFormat } from "../useApiActionParametersSchema";
import { useFieldCommonProps } from "../useFieldCommonProps";
import { OptionalParamWrapper } from "./wrappers/OptionalParamWrapper";
import { FieldChip } from "./wrappers/ReferencedField/ReferencedFieldChip";
import { ReferencedFieldWrapper } from "./wrappers/ReferencedField/ReferencedFieldWrapper";

type Transformers<T = string | number> = [
  (value: string | number | null) => DateTime | null,
  T extends string
    ? (value: DateTime | null) => string | null
    : T extends number
      ? (value: DateTime | null) => number
      : never,
];

interface RelativeTimeDiffOption {
  label: string;
  diff: number;
  unit: TimeFrequency;
}
interface TimestampTabsProps<T> {
  value: T;
  onChange: (newValue: T) => void;
  onClose: () => void;
}

const RELATIVE_TIME_DIFF_OPTIONS: RelativeTimeDiffOption[] = [
  { label: "Start of the flow", diff: 0, unit: TimeFrequency.Day },
  { label: "1 hour ago", diff: 1, unit: TimeFrequency.Hour },
  { label: "1 day ago", diff: 1, unit: TimeFrequency.Day },
  { label: "1 week ago", diff: 1, unit: TimeFrequency.Week },
  { label: "30 days ago", diff: 30, unit: TimeFrequency.Day },
  { label: "1 month ago", diff: 1, unit: TimeFrequency.Month },
  { label: "3 months ago", diff: 3, unit: TimeFrequency.Month },
  { label: "6 months ago", diff: 6, unit: TimeFrequency.Month },
  { label: "1 year ago", diff: 1, unit: TimeFrequency.Year },
];

function maybeValidDateTime(value: DateTime): DateTime | null {
  if (value.isValid) {
    return value;
  }
  return null;
}

function createTransformersFromFormat(format: string | undefined): Transformers {
  switch (true) {
    case format === TimestampFormat.UNIX_SECONDS:
      return [
        (value) => (typeof value === "number" && value !== -1 ? maybeValidDateTime(DateTime.fromSeconds(value)) : null),
        (value: DateTime | null) => value?.toUTC().toSeconds() ?? -1,
      ];
    case format === TimestampFormat.UNIX_MILLISECONDS || format === TimestampFormat.UNKNOWN:
      return [
        (value) => (typeof value === "number" && value !== -1 ? maybeValidDateTime(DateTime.fromMillis(value)) : null),
        (value: DateTime | null) => value?.toUTC().toMillis() ?? -1,
      ];
    default:
      return [
        (value) => (typeof value === "string" ? maybeValidDateTime(DateTime.fromFormat(value, format)) : null),
        (value: DateTime | null) => value?.toUTC().toFormat(format) ?? null,
      ];
  }
}

export function getRelativeTimeDiffOption(value: RelativeTimeDiffValue): RelativeTimeDiffOption | null {
  return RELATIVE_TIME_DIFF_OPTIONS.find((option) => option.unit === value.unit && option.diff === value.diff) || null;
}

const StyledStaticDateTimePicker = styled(StaticDateTimePicker)({
  "& .MuiCalendarOrClockPicker-root": {
    position: "relative",
  },
  "& .MuiPickersArrowSwitcher-root": {
    top: 5,
  },
});

const TimestampTabs: FC<TimestampTabsProps<RelativeTimeDiffValue | DateTime | null>> = ({
  value,
  onChange,
  onClose,
}) => {
  const [tabValue, setTabValue] = useState<string>("relative");
  const handleTabChange = (_, newValue: string) => {
    setTabValue(newValue);
  };

  const handleRelativeDateSelect = useCallback(
    (option: RelativeTimeDiffOption) => {
      onChange({
        builtInVariable: BuiltInVariableType.RELATIVE_TIME_DIFF,
        diff: option.diff,
        unit: option.unit,
      });
      onClose();
    },
    [onChange, onClose]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="date selection tabs" variant="fullWidth">
            <Tab
              sx={{ textTransform: "none" }}
              label="Relative date"
              value="relative"
              data-cy="timestamp-tab-relative"
            />
            <Tab
              sx={{ textTransform: "none" }}
              label="Specific date"
              value="specific"
              data-cy="timestamp-tab-specific"
            />
          </TabList>
        </Box>
        <TabPanel value="relative" sx={{ padding: 0 }}>
          <Typography variant="body2" color="textSecondary" sx={{ py: 1.5, px: 2 }}>
            All dates are relative to the CloudFlow start time
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {RELATIVE_TIME_DIFF_OPTIONS.map((option) => (
              <MenuItem
                key={option.label}
                onClick={() => {
                  handleRelativeDateSelect(option);
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Box>
        </TabPanel>
        <TabPanel value="specific" sx={{ padding: 0 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <StyledStaticDateTimePicker
              ampm={false}
              displayStaticWrapperAs="desktop"
              openTo="day"
              value={isRelativeTimeDiffValue(value) ? null : value}
              onChange={noop}
              renderInput={(params) => <TextField {...params} />}
              onAccept={(date) => {
                onChange(date as DateTime | RelativeTimeDiffValue | null);
                onClose();
              }}
            />
          </LocalizationProvider>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export const TimestampParam: FC<{
  fieldProps: FieldInputProps<RelativeTimeDiffValue | string | number | null>;
  label: string;
  inputModel: TimestampApiServiceModelDescriptor;
  onRemove?: () => void;
  disallowReferencedField?: boolean;
  renderAsNotRequired?: boolean;
}> = ({ fieldProps, label, onRemove, inputModel, disallowReferencedField, renderAsNotRequired }) => {
  const formikProps = useFormikContext();
  const [selectedValue, setSelectedValue] = useState<RelativeTimeDiffValue | DateTime | null>(null);
  const [menuOpen, handleClick, handleCloseMenu] = useToggle();
  const anchorEl = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const commonFieldProps = useFieldCommonProps(fieldProps, label, onRemove === undefined && !renderAsNotRequired);
  const [[fromModelValue, toModelValue], setTransformers] = useState<Transformers>(
    createTransformersFromFormat(inputModel.timestampFormat)
  );

  useEffect(() => {
    setTransformers(createTransformersFromFormat(inputModel.timestampFormat));
  }, [inputModel.timestampFormat]);

  useEffect(() => {
    const newInternalValue = isRelativeTimeDiffValue(fieldProps.value)
      ? fieldProps.value
      : fromModelValue(fieldProps.value);

    setSelectedValue((selectedValue) => (isEqual(selectedValue, newInternalValue) ? selectedValue : newInternalValue));
  }, [fieldProps.value, fromModelValue]);

  const commonFieldPropsWithoutOnChange = omit(commonFieldProps, "onChange");
  const onChange = useCallback(
    (v: DateTime | RelativeTimeDiffValue | null) => {
      const value = isRelativeTimeDiffValue(v) ? v : toModelValue(v);
      formikProps.setFieldValue(fieldProps.name, value);
    },
    [fieldProps.name, formikProps, toModelValue]
  );

  const onDelete = useCallback(() => {
    formikProps.setFieldValue(fieldProps.name, null);
    inputRef.current?.focus();
  }, [fieldProps.name, formikProps]);

  const renderChipLabel = useCallback(() => {
    if (isRelativeTimeDiffValue(selectedValue)) {
      return getRelativeTimeDiffOption(selectedValue)?.label;
    }
    return selectedValue?.toLocaleString(DateTime.DATETIME_MED);
  }, [selectedValue]);

  const dateTimePicker = (
    <FormControl fullWidth required={commonFieldProps.required} error={commonFieldProps.error}>
      <InputLabel size="small">{label}</InputLabel>
      <OutlinedInput
        {...commonFieldPropsWithoutOnChange}
        ref={anchorEl}
        size="small"
        readOnly
        onClick={handleClick}
        value="&nbsp;"
        startAdornment={<FieldChip labelSlot={renderChipLabel} onDelete={onDelete} />}
        endAdornment={
          <InputAdornment position="end">
            <CalendarMonthIcon sx={{ cursor: "pointer" }} data-cy="timestamp-calendar-icon" />
          </InputAdornment>
        }
        inputRef={inputRef}
      />
      <Menu anchorEl={anchorEl.current} open={menuOpen} onClose={handleCloseMenu}>
        <TimestampTabs value={selectedValue} onChange={onChange} onClose={handleCloseMenu} />
      </Menu>
      {commonFieldProps.helperText && (
        <Typography variant="caption" color={commonFieldProps.error ? "error" : "textSecondary"}>
          {commonFieldProps.helperText}
        </Typography>
      )}
    </FormControl>
  );

  return (
    <OptionalParamWrapper onRemove={onRemove}>
      {disallowReferencedField ? (
        dateTimePicker
      ) : (
        <ReferencedFieldWrapper commonFieldProps={commonFieldProps} model={inputModel}>
          {dateTimePicker}
        </ReferencedFieldWrapper>
      )}
    </OptionalParamWrapper>
  );
};
